<template>
  <div class="analytic-report">
    <template v-if="cameraInfo && cameraInfo.isAvailableArchive()">
      <!-- Настройки отчета -->
      <div class="analytic-report__settings">
        <SmartSelect
          v-model="timeBucket"
          :options="settingsTimeBucket"
          :caption="$t('reportType')"
          width="fill"
          size="s"
          class="analytic-report__settings-control analytic-report__settings-control_small"
          @input="loadReportTimeBucket"
        />
        <SmartInputDate
          ref="archiveFromInput"
          v-model="archiveFrom"
          :config-flat-pickr="{ maxDate: new Date()}"
          :caption="$t('startDate')"
          size="s"
          :locale="this.locale"
          class="analytic-report__settings-control analytic-report__settings-control_small"
        />
        <SmartInputDate
          ref="archiveToInput"
          v-model="archiveTo"
          :config-flat-pickr="{maxDate: new Date()}"
          :caption="$t('endDate')"
          size="s"
          :locale="this.locale"
          class="analytic-report__settings-control analytic-report__settings-control_small"
        />
        <CamsButton
          class="analytic-report__settings-control"
          priority="primary"
          size="s"
          type="button"
          @click="downloadTableAsCSVReport()"
        >
          {{ $t('downloadReport') }}
        </CamsButton>
        <div class="analytic-report__view-container">
          <span class="analytic-report__view-label">Вид:</span>
          <!-- Переключатель вида отчета -->
          <div class="analytic-report__view-toggle">
            <CamsButton
              :class="{'analytic-report__view-button--active': viewType === 'table'}"
              class="analytic-report__view-button"
              priority="secondary"
              type="button"
              icon-type="only"
              @click="viewType = 'table'"
            >
              <svg class="analytic-report__view-icon">
                <use xlink:href="#icon-table-view" />
              </svg>
            </CamsButton>
            <CamsButton
              :class="{'analytic-report__view-button--active': viewType === 'chart'}"
              class="analytic-report__view-button"
              priority="secondary"
              type="button"
              icon-type="only"
              @click="viewType = 'chart'"
            >
              <svg class="analytic-report__view-icon">
                <use xlink:href="#icon-chart-view" />
              </svg>
            </CamsButton>
          </div>
        </div>
      </div>
      <SpinnerLoading v-if="isLoading" class="loader_center" color="blue" />
      <!-- График -->
      <div v-if="viewType === 'chart' && hasChartData && !isLoading" class="people-report-chart">
        <div class="people-report-chart__summary">
          <div class="summary-card summary-card_in">
            <div class="summary-card__value">
              {{ totalIn }} Вошло
            </div>
          </div>
          <div class="summary-card summary-card_out">
            <div class="summary-card__value">
              {{ totalOut }} Вышло
            </div>
          </div>
        </div>
        <apexchart
          type="bar"
          height="400"
          :options="chartOptions"
          :series="chartSeries"
        />
      </div>
      <!-- Пагинация (только для таблицы) -->
      <div v-if="viewType === 'table'" class="analytic-report__pagination">
        <paginate
          v-model="currentPage"
          class="analytic-report__pagination_smart-pagination"
          :click-handler="loadReportTimeBucket"
          :page-count="allPages"
          :page-range="5"
          active-class="smart-pagination__page_active"
          break-view-class="smart-pagination__page_collapse"
          container-class="smart-pagination"
          next-class="smart-pagination__page smart-pagination__next-prev"
          next-link-class="smart-pagination__page-link small"
          next-text="<svg class='icon icon-arrow-left'><use xlink:href='#icon-arrow-left'></use></svg>"
          page-class="smart-pagination__page"
          page-link-class="smart-pagination__page-link small"
          prev-class="smart-pagination__page smart-pagination__next-prev"
          prev-link-class="smart-pagination__page-link small"
          prev-text="<svg class='icon icon-arrow-right'><use xlink:href='#icon-arrow-right'></use></svg>"
        />
        <SmartSelect
          v-model="pageSize"
          :options="optionsPageSize"
          :caption="$t('itemsPerPage')"
          width="fill"
          class="analytic-report__pagination_smart-select-pagination"
          size="s"
          @input="setPageSize(pageSize)"
        />
      </div>
      <!-- Таблица -->
      <div v-if="viewType === 'table'" class="analytic-report__table cams-table-wrapper">
        <SpinnerLoading v-if="isLoadingReport" class="loader_center" color="blue" />
        <!-- Таблица для агрегированных отчетов -->
        <table v-if="timeBucket!=='defaultReport'" class="cams-table">
          <tr>
            <th class="cams-table__cell cams-table__cell_fixed-width" />
            <th class="cams-table__cell" style="width: 400px">
              {{ $t('date') }}
            </th>
            <th class="cams-table__cell">
              {{ $t('inCount') }}
            </th>
            <th class="cams-table__cell">
              {{ $t('outCount') }}
            </th>
          </tr>
          <template v-for="message in paginatedBucketResults">
            <tr :key="message.id" :class="{'warning warning-bg': message.alarm}">
              <td class="cams-table__cell cams-table__cell_fixed-width" />
              <td class="cams-table__cell">
                {{ message.start | localDateTime }} - {{ message.end | localDateTime }}
              </td>
              <td class="cams-table__cell">
                {{ message.in_count }}
              </td>
              <td class="cams-table__cell">
                {{ message.out_count }}
              </td>
            </tr>
          </template>
        </table>
        <!-- Таблица для детального отчета -->
        <table v-else-if="messages" class="cams-table">
          <tr>
            <th class="cams-table__cell cams-table__cell_fixed-width" />
            <th class="cams-table__cell">
              {{ $t('text') }}
            </th>
            <th class="cams-table__cell">
              {{ $t('date') }}
            </th>
          </tr>
          <template v-for="message in messages">
            <tr :key="message.id" :class="{'warning warning-bg': message.alarm}">
              <td class="cams-table__cell cams-table__cell_fixed-width">
                <div class="analytic-report__event-actions">
                  <CamsButton
                    v-show="message.date >= minVideoArchiveFrom"
                    icon-type="only"
                    :title="$t('eventVideo')"
                    type="button"
                    @click="playEventStart(message, cameraInfo)"
                  >
                    <svg>
                      <use xlink:href="#icon-video-preview" />
                    </svg>
                  </CamsButton>
                  <CamsButton
                    v-show="message.date >= minVideoArchiveFrom"
                    icon-type="only"
                    :title="$t('downloadEventVideo')"
                    type="button"
                    @click="downloadEventVideo(message, cameraInfo)"
                  >
                    <svg>
                      <use xlink:href="../../../assets/img/icons.svg#cloud-archive" />
                    </svg>
                  </CamsButton>
                </div>
              </td>
              <td class="cams-table__cell">
                {{ message.text === 'in' ? $t('entrance') : $t('exit') }}
              </td>
              <td class="cams-table__cell">
                {{ message.date | localDateTime }}
              </td>
            </tr>
          </template>
        </table>
        <template v-else>
          <p>{{ $t('noEvents') }}</p>
        </template>
      </div>
    </template>
    <template v-else>
      <p>{{ $t('archiveError') }}</p>
    </template>
    <iframe :src="downloadUrl" frameborder="0" height="1" width="1" />
  </div>
</template>

<script>
import {analyticReportMixin} from "@/components/oneScreen/reports/mixins.js";
import {downloadCSV} from "@/utils/helpers.js";
import {
  BASE_CHART_OPTIONS,
  ACTION_LOAD_PEOPLE_COUNT_REPORT,
  ACTION_LOAD_PEOPLE_COUNT_REPORT_COUNT_BUCKET,
} from "@/store/analytics/peopleCount/index.js";
import VueApexCharts from "vue-apexcharts";


/**
 * Отчет по подсчету посетителей.
 * Компонент отображает статистику входа/выхода людей в различных форматах:
 * - График (столбчатая диаграмма)
 * - Таблица (с пагинацией)
 * Поддерживает различные периоды агрегации данных: почасовой, ежедневный, еженедельный, ежемесячный.
 */
export default {
  name: "PeopleReportScreen",
  mixins: [
    analyticReportMixin,
  ],
  components: {
    apexchart: VueApexCharts,
  },
  data() {
    return {
      archiveFrom: null,
      archiveTo: null,
      searchText: null,
      timeBucket: "defaultReport",
      settingsTimeBucket: null,
      calendarOpen: null,
      viewType: "chart",
      allBucketResults: [],
      allDefaultReportData: [],
    };
  },
  computed: {
    hasChartData() {
      if (this.timeBucket === "defaultReport") {
        return this.allDefaultReportData && this.allDefaultReportData.length > 0;
      } else {
        return this.allBucketResults && this.allBucketResults.length > 0;
      }
    },
    chartOptions() {
      return {
        ...BASE_CHART_OPTIONS,
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: this.getColumnWidth(),
            borderRadius: 2,
          },
        },
        xaxis: {
          ...BASE_CHART_OPTIONS.xaxis,
          categories: this.chartCategories,
          labels: {
            ...BASE_CHART_OPTIONS.xaxis.labels,
            formatter: this.formatXAxisLabel,
          },
        },
        yaxis: {
          title: {
            text: "Количество людей"
          }
        },
      };
    },
    paginatedBucketResults() {
      if (!this.allBucketResults.length) return [];
      const startIndex = (this.currentPage - 1) * this.pageSize;
      const endIndex = startIndex + this.pageSize;
      return this.allBucketResults.slice(startIndex, endIndex);
    },

    bucketPagesCount() {
      if (!this.allBucketResults.length) return 1;
      return Math.ceil(this.allBucketResults.length / this.pageSize);
    },

    chartDataForGraph() {
      if (!this.hasChartData) return [];
      if (this.timeBucket === "defaultReport") {
        return [...this.allDefaultReportData].reverse();
      } else {
        return [...this.allBucketResults];
      }
    },
    chartCategories() {
      if (!this.hasChartData) return [];

      if (this.timeBucket === "defaultReport") {
        return this.chartDataForGraph.map((m, index) =>
          `Событие ${index + 1}`
        );
      } else {
        return this.chartDataForGraph.map((m) =>
          this.formatChartLabel(m.start, m.end)
        );
      }
    },
    selectedRangeDays() {
      if (!this.clearArchiveFrom || !this.clearArchiveTo) return 0;
      const diff = this.clearArchiveTo.getTime() - this.clearArchiveFrom.getTime();
      return diff / (1000 * 3600 * 24);
    },

    isTooBigDefaultChartRange() {
      return this.timeBucket === 'defaultReport'
        && this.viewType === 'chart'
        && this.selectedRangeDays > 7;
    },
    chartSeries() {
      if (!this.hasChartData) return [];

      if (this.timeBucket === "defaultReport") {
        return [
          {
            name: this.$t("entrance"),
            data: this.chartDataForGraph.map(m => m.text === 'in' ? 1 : 0),
          },
          {
            name: this.$t("exit"),
            data: this.chartDataForGraph.map(m => m.text === 'out' ? 1 : 0),
          }
        ];
      } else {
        return [
          {
            name: this.$t("entrance"),
            data: this.chartDataForGraph.map((m) => m.in_count),
          },
          {
            name: this.$t("exit"),
            data: this.chartDataForGraph.map((m) => m.out_count),
          },
        ];
      }
    },

    totalIn() {
      if (!this.hasChartData) return 0;

      if (this.timeBucket === "defaultReport") {
        return this.allDefaultReportData.filter(m => m.text === 'in').length;
      } else {
        return this.allBucketResults.reduce((sum, m) => sum + m.in_count, 0);
      }
    },

    totalOut() {
      if (!this.hasChartData) return 0;

      if (this.timeBucket === "defaultReport") {
        return this.allDefaultReportData.filter(m => m.text === 'out').length;
      } else {
        return this.allBucketResults.reduce((sum, m) => sum + m.out_count, 0);
      }
    },

    clearArchiveFrom() {
      return this.archiveFrom && this.archiveTo
        ? new Date(Math.min(this.archiveFrom.getTime(), this.archiveTo.getTime()))
        : null;
    },

    clearArchiveTo() {
      return this.archiveFrom && this.archiveTo
        ? new Date(Math.max(this.archiveFrom.getTime(), this.archiveTo.getTime()))
        : null;
    },
  },
  watch: {
    archiveFrom(newStart) {
      if (newStart && this.archiveTo && this.archiveTo < newStart) {
        this.archiveTo = newStart;
      }
      this.debouncedLoadReportBucket();
    },
    archiveTo(newEnd) {
      if (newEnd && this.archiveFrom && newEnd < this.archiveFrom) {
        this.archiveTo = this.archiveFrom;
      }
      this.debouncedLoadReportBucket();
    },
    searchText() {
      this.debouncedLoadReportBucket();
    },
    currentPage() {
      this.debouncedLoadReportBucket();
    },
    pageSize() {
      this.debouncedLoadReportBucket();
    },
  },

  created() {
    this.debouncedLoadReportBucket = _.debounce(this.loadReportTimeBucket, 350);
    this.getTimePeriondsForSelect();
  },

  methods: {
    /**
     * Рассчитывает оптимальную ширину столбцов для графика в зависимости от количества данных
     * @returns {string} - CSS значение ширины столбца
     */    getColumnWidth() {
      if (this.timeBucket === "1") { // почасовой
        const dataLength = this.chartDataForGraph.length;
        if (dataLength > 72) return "15%";   // 3+ дней - очень узкие
        if (dataLength > 48) return "20%";   // 2-3 дня
        if (dataLength > 24) return "25%";   // 1-2 дня
        if (dataLength > 12) return "35%";   // 12-24 часа
        return "45%";                        // до 12 часов
      }
      return "70%";
    },

    /**
     * Форматирует подписи на оси X графика
     * Для почасовых данных показывает только каждую N-ю метку для улучшения читаемости
     * @param {string} value - исходное значение метки
     * @param {number} timestamp - временная метка
     * @param {Object} opts - опции apexcharts
     * @returns {string} - отформатированная метка или пустая строка для скрытия
     */
    formatXAxisLabel(value, timestamp, opts) {
      // Проверяем, что opts существует и содержит dataPointIndex
      if (!opts || typeof opts.dataPointIndex === 'undefined') {
        return value || '';
      }

      if (this.timeBucket === "1") {
        // Для почасовых данных показываем только каждую N-ю метку
        const index = opts.dataPointIndex;
        const total = this.chartCategories ? this.chartCategories.length : 0;
        // Показываем подписи реже при большом количестве данных
        let showEvery = 1;
        if (total > 48) showEvery = 6; // каждые 6 часов
        else if (total > 24) showEvery = 3; // каждые 3 часа
        else if (total > 12) showEvery = 2; // каждые 2 часа
        if (index % showEvery !== 0) return '';
      }
      return value || '';
    },

    // Обновление настроек графика
    updateChartOptions() {
      this.chartOptions = {
        ...this.chartOptions,
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: this.getColumnWidth(),
            borderRadius: 2,
          },
        },
        xaxis: {
          ...this.chartOptions.xaxis,
          categories: this.chartCategories,
          labels: {
            ...this.chartOptions.xaxis.labels,
            formatter: this.formatXAxisLabel
          }
        },
      };
    },
    /**
     * Форматирует метки для отображения на графике в зависимости от выбранного периода агрегации
     * @param {string} start - начальная дата периода
     * @param {string} end - конечная дата периода
     * @returns {string} - отформатированная метка для отображения
     */
    formatChartLabel(start, end) {
      const startDate = new Date(start);
      const endDate = new Date(end);

      switch(this.timeBucket) {
      case '1': // Почасовой
        // Используем реальные даты из данных
        return `${startDate.getDate().toString().padStart(2, '0')}.${(startDate.getMonth() + 1).toString().padStart(2, '0')} ${startDate.getHours().toString().padStart(2, '0')}:00`;
      case '24': // По дням
        return `${startDate.getDate().toString().padStart(2, '0')}.${(startDate.getMonth() + 1).toString().padStart(2, '0')}.${startDate.getFullYear()}`;
      case '168': // По неделям
        // eslint-disable-next-line no-case-declarations
        const weekStart = `${startDate.getDate().toString().padStart(2, '0')}.${(startDate.getMonth() + 1).toString().padStart(2, '0')}`;
        // eslint-disable-next-line no-case-declarations
        const weekEnd = `${endDate.getDate().toString().padStart(2, '0')}.${(endDate.getMonth() + 1).toString().padStart(2, '0')}`;
        return `${weekStart}-${weekEnd}`;
      case '720': // По месяцам
        // eslint-disable-next-line no-case-declarations
        const months = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'];
        return `${months[startDate.getMonth()]} ${startDate.getFullYear()}`;

      default:
        return this.localDateHours(start);
      }
    },

    /**
     * Агрегирует данные детального отчета в почасовые интервалы
     * Группирует события входа/выхода по часам для построения агрегированного графика
     * @param {Array} events - массив событий входа/выхода
     * @returns {Array} - массив агрегированных данных по часам
     */
    aggregateDefaultReportData(events) {
      const hourlyMap = {};
      events.forEach((event) => {
        const m = moment.tz(event.date, moment.tz.guess());
        m.startOf("hour");
        const key = m.valueOf();
        if (!hourlyMap[key]) {
          hourlyMap[key] = {
            start: m.toDate(),
            end: moment(m).add(1, "hour").toDate(),
            in_count: 0,
            out_count: 0,
          };
        }
        if (event.text === "in") {
          hourlyMap[key].in_count += 1;
        } else if (event.text === "out") {
          hourlyMap[key].out_count += 1;
        }
      });
      return Object.values(hourlyMap).sort((a, b) => a.start - b.start);
    },
    /**
     * Инициализирует доступные периоды агрегации для выпадающего списка
     * @returns {Object} - объект с настройками периодов агрегации
     */
    getTimePeriondsForSelect() {
      this.settingsTimeBucket = {
        defaultReport: this.$t('defaultReport'),
        "1": this.$t('hourly'),
        "24": this.$t('daily'),
        "168": this.$t('weekly'),
        "720": this.$t('monthly')
      };
      return this.settingsTimeBucket;
    },

    resetFilters() {
      this.isLoading = true;
      const archiveTo = new Date();
      const archiveFrom = new Date(archiveTo.getTime());
      archiveFrom.setHours(archiveTo.getHours() - 1);
      this.archiveFrom = archiveFrom;
      this.archiveTo = archiveTo;
      this.searchText = "";
      this.currentPage = 1;
      this.viewType = "chart";
      this.isLoading = false;
    },
    /**
     * Основной метод загрузки данных отчета
     * Выбирает стратегию загрузки в зависимости от выбранного типа отчета
     * Обрабатывает ошибки и показывает предупреждения при большом объеме данных
     */
    async loadReportTimeBucket() {
      if (this.isLoading) return;
      if (!this.clearArchiveFrom || !this.clearArchiveTo) return;
      if (this.isTooBigDefaultChartRange) {
        this.$camsdals.alert("Слишком большой объем данных для графика, используйте агрегированные отчеты");
        return;
      }
      this.isLoading = true;
      try {
        if (this.timeBucket === 'defaultReport') {
          await this.loadDefaultReport();
        } else {
          await this.loadBucketReport();
        }
      } catch (error) {
        console.error('Ошибка загрузки отчета:', error);
        alert('Произошла ошибка при загрузке отчета. Попробуйте выбрать меньший период времени или используйте агрегированные отчеты.');
      } finally {
        this.isLoading = false;
      }
    },
    /**
     * Загружает детальный отчет (без агрегации)
     * Загружает данные для таблицы с пагинацией и отдельно для графика
     */
    async loadDefaultReport() {
      // Таблица
      const {messages: paginatedMessages, response: paginatedResponse} =
        await this.$store.dispatch(`analytics/peopleCount/${ACTION_LOAD_PEOPLE_COUNT_REPORT}`, {
          cameraNumber: this.cameraInfo.number,
          archiveFrom: this.clearArchiveFrom,
          archiveTo: this.clearArchiveTo,
          query: this.searchText,
          page: this.currentPage,
          page_size: this.pageSize,
        });
      this.messages = paginatedMessages || [];
      this.allPages = paginatedResponse.page.all || 1;
      this.currentPage = paginatedResponse.page.current || 1;
      if (this.currentPage > this.allPages) this.currentPage = 1;
      // Данные для графика — только если нужен график
      if (this.viewType === 'chart') {
        try {
          const {messages} = await this.$store.dispatch(`analytics/peopleCount/${ACTION_LOAD_PEOPLE_COUNT_REPORT}`, {
            cameraNumber: this.cameraInfo.number,
            archiveFrom: this.clearArchiveFrom,
            archiveTo: this.clearArchiveTo,
            query: this.searchText,
            page: 1,
            page_size: 1000,
          });
          this.allDefaultReportData = messages || [];
        } catch (error) {
          console.error('Ошибка загрузки данных для графика:', error);
          this.allDefaultReportData = [];
        }
      } else {
        this.allDefaultReportData = [];
      }
    },
    /**
     * Загружает агрегированный отчет по выбранному временному интервалу
     * Используется для почасовой, ежедневной, еженедельной и ежемесячной агрегации
     */
    async loadBucketReport() {
      const bucketData = await this.$store.dispatch(`analytics/peopleCount/${ACTION_LOAD_PEOPLE_COUNT_REPORT_COUNT_BUCKET}`, {
        cameraNumber: this.cameraInfo.number,
        archiveFrom: this.clearArchiveFrom,
        archiveTo: this.clearArchiveTo,
        timeBucket: this.timeBucket,
      });
      this.allBucketResults = (bucketData.results || []).reverse();
      this.allPages = this.bucketPagesCount;
      this.currentPage = 1;
      this.messages = {
        results: this.paginatedBucketResults,
      };
    },
    /**
     * Форматирует дату в локальный формат с временем
     * @param {string} date - дата для форматирования
     * @returns {string} - отформатированная дата в формате "DD.MM.YYYY HH:mm"
     */
    localDateHours(date) {
      if (!date) {
        return "-";
      }
      const d = new Date(date);
      return `${d.getDate().toString().padStart(2, "0")}.${(d.getMonth() + 1)
        .toString()
        .padStart(2, "0")}.${d.getFullYear()} ${d.getHours().toString().padStart(2, "0")}:${d
        .getMinutes()
        .toString()
        .padStart(2, "0")}`;
    },
    /**
     * Скачивание архива в виде csv файла.
     *
     */
    async downloadTableAsCSVReport() {
      this.isLoading = true;
      let dateFrom = moment.tz(this.clearArchiveFrom, moment.tz.guess()).format("DD.MM.YYYY_HH.mm");
      let dateTo = moment.tz(this.clearArchiveTo, moment.tz.guess()).format("DD.MM.YYYY_HH.mm");
      const filenameBuilder = `${this.$t('peopleCountReport')}_${dateFrom}-${dateTo}_${this.cameraInfo.title}.csv`;
      if (this.timeBucket === "defaultReport") {
        const apiAction = `analytics/peopleCount/${ACTION_LOAD_PEOPLE_COUNT_REPORT}`;
        const params = {
          cameraNumber: this.cameraInfo.number,
          archiveFrom: this.clearArchiveFrom,
          archiveTo: this.clearArchiveTo,
        };
        const headers = [this.$t('text'), this.$t('date')];
        const rowBuilder = (message) => {
          return `${message.text === 'in' ? this.$t('entrance') : this.$t('exit')},${this.$options.filters.localDateTime(message.date)}\r\n`;
        };
        await this.downloadTableAsCSV(apiAction, params, headers, filenameBuilder, rowBuilder);
      } else {
        let csvText;
        let headers = [this.$t('date'), this.$t('inCount'), this.$t('outCount')];
        csvText = `${headers.join(",")}\r\n`;
        this.allBucketResults.forEach((message) => {
          const formattedDate = `${this.localDateHours(message.start)} - ${this.localDateHours(
            message.end
          )}`;
          csvText += `${formattedDate},${message.in_count},${message.out_count}\r\n`;
        });
        downloadCSV(filenameBuilder, csvText);
      }

      this.isLoading = false;
    },
  },
};
</script>

<style lang="scss">
@use "./analytic-report.scss";
</style>
